import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BranchPriceItem,
  BulkUpdateBranchPricesInput,
  CopyBranchPricesInput,
  OrgNodeItem,
  Paginated,
  PaginationQuery,
  SellingRateColumn,
} from '@tiles-erp/shared-types';
import { apiFetch } from '../../lib/api-client';
import type { ProductFilters } from './api';

const KEY = 'branch-prices';

export function useBranches() {
  return useQuery({
    queryKey: ['branches', 'options'],
    queryFn: () => apiFetch<Paginated<OrgNodeItem>>('/branches?page=1&pageSize=200'),
    staleTime: 60_000,
    select: (data) => data.items,
  });
}

export function useBranchPrices(
  branchId: string | undefined,
  query: PaginationQuery,
  filters: ProductFilters,
) {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
    branchId: branchId ?? '',
  });
  if (query.search) params.set('search', query.search);
  if (filters.brandId) params.set('brandId', filters.brandId);
  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  if (filters.seriesId) params.set('seriesId', filters.seriesId);
  if (filters.sizeMm) params.set('sizeMm', filters.sizeMm);
  if (filters.piecesPerBox !== undefined) params.set('piecesPerBox', String(filters.piecesPerBox));
  return useQuery({
    queryKey: [KEY, branchId ?? null, query, filters],
    queryFn: () => apiFetch<Paginated<BranchPriceItem>>(`/branch-prices?${params.toString()}`),
    enabled: Boolean(branchId),
    placeholderData: (previous) => previous,
  });
}

export function useBulkUpdateBranchPrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkUpdateBranchPricesInput) =>
      apiFetch<BranchPriceItem[]>('/branch-prices/bulk', {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useSellingRateColumns() {
  return useQuery({
    queryKey: [KEY, 'rate-columns'],
    queryFn: () => apiFetch<SellingRateColumn[]>('/branch-prices/rate-columns'),
  });
}

export function useCreateSellingRateColumn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiFetch<SellingRateColumn>('/branch-prices/rate-columns', {
      method: 'POST', body: JSON.stringify({ name }),
    }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useRenameSellingRateColumn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiFetch<SellingRateColumn>(`/branch-prices/rate-columns/${id}`, {
        method: 'PATCH', body: JSON.stringify({ name }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useDeleteSellingRateColumn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/branch-prices/rate-columns/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useCopyBranchPrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CopyBranchPricesInput) =>
      apiFetch<{ copied: number }>('/branch-prices/copy', {
        method: 'POST', body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [KEY] }),
  });
}
